import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { LoginModal } from "@/components/auth/login-modal";
import { useAccountSummary } from "@/solana/hooks/use-account-summary";
import { usePositions } from "@/solana/hooks/use-positions";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { useState, useMemo } from "react";
import { useRealTimeUnrealizedPnL } from "@/backend/store";
import { useTradingSettings } from "@/features/trading-settings/trading-settings-context";
import { usePrivacyMode } from "@/hooks/use-privacy-mode";
import {
  ShieldCheck,
  ShieldAlert,
  Skull,
} from "lucide-react";
import { 
  calculateHealth, 
  getWarningLevelFromHealth,
  type PositionForHealth 
} from "./margin-health-bar";

// Warning config (simplified - no caution level)
const warningConfig = {
  safe: { color: "bg-bid-foreground", bgColor: "bg-bid/20", textColor: "text-bid-foreground", icon: ShieldCheck, label: "Healthy" },
  warning: { color: "bg-orange-500", bgColor: "bg-orange-500/20", textColor: "text-orange-500", icon: ShieldAlert, label: "Warning" },
  danger: { color: "bg-ask-foreground", bgColor: "bg-ask/20", textColor: "text-ask-foreground", icon: Skull, label: "Danger" },
};

export const MarginInfo = () => {
  const { isAuthenticated } = useUnifiedAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const { summary, isLoading } = useAccountSummary();
  const { positions } = usePositions();
  const { accountMode } = useTradingSettings();
  const { privacyMode } = usePrivacyMode();

  // Subscribe to real-time unrealized PnL (calculated in store)
  const realTimeUnrealizedPnL = useRealTimeUnrealizedPnL();

  // Calculate real-time used margin from open positions
  // Each position's margin = (position size * entry price) / leverage
  const realTimeUsedMargin = useMemo(() => {
    if (positions.length === 0) return 0;
    return positions.reduce((total, position) => {
      return total + (position.size * position.entryPrice) / position.leverage;
    }, 0);
  }, [positions]);

  // Calculate values using real-time PnL (updates as prices change)
  // Use real-time unrealized PnL if we have positions, otherwise fall back to backend value
  const unrealizedPnl = positions.length > 0 ? realTimeUnrealizedPnL : (summary?.unrealizedPnl ?? 0);
  const equity = summary ? summary.collateral + unrealizedPnl : 0;
  const openPositionsCount = positions.length;

  // Calculate real-time margin available
  // Formula: Total Equity - Used Margin
  const realTimeMarginAvailable = useMemo(() => {
    if (!summary) return 0;
    // Always use calculated margin (it's 0 when no positions, correct when positions exist)
    return equity - realTimeUsedMargin;
  }, [summary, equity, realTimeUsedMargin]);

  // Default to "cross" if accountMode is not yet loaded
  const effectiveAccountMode = accountMode ?? "cross";

  // Transform positions for health calculation
  const positionsForHealth: PositionForHealth[] = useMemo(() => {
    return positions.map(position => ({
      size: position.size,
      entryPrice: position.entryPrice,
      leverage: position.leverage,
      margin: position.margin ?? (position.size * position.entryPrice) / position.leverage,
    }));
  }, [positions]);

  // Calculate account health using the new formula (0-1 scale)
  const accountHealth = useMemo(() => {
    return calculateHealth(equity, realTimeUsedMargin, positionsForHealth, effectiveAccountMode);
  }, [equity, realTimeUsedMargin, positionsForHealth, effectiveAccountMode]);

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <p className="text-muted-foreground text-sm">
          Log in to view account info
        </p>
        <button
          onClick={() => setLoginModalOpen(true)}
          className="text-primary text-sm mt-2 hover:underline cursor-pointer"
        >
          Click here
        </button>
        <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="px-3 pt-1.5 pb-2 space-y-2">
      {/* Account Overview - Exchange Style */}
      {summary ? (
        <>
          {/* Top Section: 2-Column Layout */}
          <div className="grid grid-cols-2 gap-3">
            {/* Left Column: Total Equity */}
            <div className="space-y-px">
              <div className="text-muted-foreground text-xs">
                Total Equity
              </div>
              <div className="text-sm font-medium tracking-tight">
                {privacyMode ? "****" : `$${formatCurrency(equity, 2)}`}
              </div>
            </div>

            {/* Right Column: Account Health */}
            <div className="space-y-px">
              <div className="text-muted-foreground text-xs">
                Account Health
              </div>
              {realTimeUsedMargin > 0 ? (
                (() => {
                  try {
                    // Health is 0-1, convert to percentage for display
                    const fillPercent = Math.min(100, Math.max(0, accountHealth * 100));
                    
                    // Ensure fillPercent is valid
                    const safeFillPercent = isFinite(fillPercent) ? fillPercent : 0;
                    
                    const level = getWarningLevelFromHealth(accountHealth);
                    const config = warningConfig[level];
                  
                    // Calculate how many bars to fill (out of 10)
                    const totalBars = 10;
                    const filledBars = Math.ceil((safeFillPercent / 100) * totalBars);
                  
                    return (
                      <div className="flex items-center gap-2 mt-0.5">
                        {/* Battery-style indicator with vertical bars */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: totalBars }).map((_, index) => (
                            <div
                              key={index}
                              className={cn(
                                "h-3 w-1.5 rounded-[2px] transition-all duration-300",
                                index < filledBars 
                                  ? cn(config.color, "opacity-90") 
                                  : cn(config.color, "opacity-30")
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  } catch (error) {
                    console.error("Error rendering account health:", error);
                    // Fallback UI on error
                    return (
                      <div className="text-sm text-muted-foreground">
                        —
                      </div>
                    );
                  }
                })()
              ) : (
                <div className="text-lg font-bold tracking-tight text-muted-foreground">
                  —
                </div>
              )}
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-3 pt-1.5 border-t border-border/50">
            <div className="space-y-px">
              <div className="text-xs text-muted-foreground">
                Account Leverage
              </div>
              <div className="text-xs font-medium">
                {summary.accountLeverage > 0 ? `${summary.accountLeverage.toFixed(2)}x` : "—"}
              </div>
            </div>

            <div className="space-y-px">
              <div className="text-xs text-muted-foreground">
                Open Positions
              </div>
              <div className="text-xs font-medium">
                {openPositionsCount}
              </div>
            </div>

            <div className="space-y-px">
              <div className="text-xs text-muted-foreground">
                Margin Available
              </div>
              <div className="text-xs font-medium text-bid-foreground">
                {privacyMode ? "****" : `$${formatCurrency(realTimeMarginAvailable, 2)}`}
              </div>
            </div>

            <div className="space-y-px">
              <div className="text-xs text-muted-foreground">
                Margin Used
              </div>
              <div className="text-xs font-medium">
                {privacyMode ? "****" : `$${formatCurrency(realTimeUsedMargin, 2)}`}
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground">
            No account data available
          </p>
        </div>
      )}
    </div>
  );
};
